<template>
  <button
    type="button"
    class="disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
    :disabled="disable"
    :class="determineClasses"
    :aria-label="buttonLabel"
  >
    <div
      class="flex items-center"
      :class="[{ 'flex-row-reverse': showSpinner }, justify]"
    >
      <div v-if="$slots['icon-left']" class="mr-2">
        <slot name="icon-left" />
      </div>
      <slot>
        <span v-if="!noLabel" :class="{ 'ml-1': showSpinner }">{{
          label
        }}</span>
      </slot>
      <div v-if="$slots['icon-right']" class="ml-2">
        <slot name="icon-right" />
      </div>
      <BaseSpinner
        v-if="showSpinner"
        :customClass="[
          'h-3 w-3',
          [ButtonType.PRIMARY, ButtonType.ERROR, ButtonType.WARNING].includes(
            type,
          )
            ? 'text-default'
            : 'text-secondary-800',
          loaderClass,
        ]"
      />
    </div>
  </button>
</template>

<script lang="ts" setup>
import { PropType, toRefs, computed } from "vue";
import { ButtonType, ButtonSize } from "@/helpers/control-types";
const props = defineProps({
  type: {
    type: String as PropType<ButtonType>,
    default: ButtonType.DEFAULT as ButtonType,
  },
  size: {
    type: String as PropType<ButtonSize>,
    default: ButtonSize.md,
  },
  disable: {
    type: Boolean,
    default: false,
  },
  showSpinner: {
    type: Boolean,
    default: false,
  },
  loaderClass: {
    type: String,
  },
  label: {
    type: String,
    default: "Label",
    required: false,
  },
  noLabel: {
    type: Boolean,
    default: false,
    required: false,
  },
  fromAvatar: {
    type: Boolean,
    default: false,
  },
  justify: {
    type: String,
    default: "justify-around",
    validate: (val: string) =>
      [
        "justify-around",
        "justify-between",
        "justify-center",
        "justify-normal",
        "justify-end",
        "justify-start",
      ].includes(val),
  },
  buttonLabel: {
    type: String,
    default: "",
  },
});
const { type, size } = toRefs(props);
const sizeClasses: { [key in ButtonSize]: string } = {
  [ButtonSize.xs]: "px-2.5 py-1 text-xs font-medium",
  [ButtonSize.sm]: "px-3 py-1.5 text-sm font-medium leading-4",
  [ButtonSize.md]: `px-4 ${
    props.fromAvatar ? "py-1.5" : "py-2"
  } text-sm font-medium`,
  [ButtonSize.lg]: "px-4 py-2.5 text-base font-medium",
  [ButtonSize.xl]: "px-6 py-3 text-base font-medium",
};

const typeClasses: { [key in ButtonType]: string } = {
  [ButtonType.PRIMARY]:
    "rounded-md border border-transparent bg-primary-600 text-default shadow-sm hover:bg-primary-700 transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2",
  [ButtonType.SECONDARY]:
    "rounded-md border border-transparent bg-primary-100 text-primary-700 hover:bg-primary-200",
  [ButtonType.DEFAULT]:
    "rounded-md border border-secondary-300 bg-default text-secondary-700 hover:bg-secondary-50",
  [ButtonType.ROUND]:
    "rounded-full border border-transparent bg-primary-600 text-default shadow-sm hover:bg-primary-700",
  [ButtonType.LINK]: "rounded-md text-primary-600 hover:text-primary-900",
  [ButtonType.ERROR]:
    "rounded-md border border-transparent bg-red-600 text-default shadow-sm hover:bg-red-800 focus:ring-2 focus:ring-offset-1 focus:ring-red-500",
  [ButtonType.WARNING]:
    "rounded-md border border-transparent bg-yellow-600 text-default shadow-sm hover:bg-yellow-800 focus:ring-2 focus:ring-offset-1 focus:ring-yellow-500",
  [ButtonType.NONE]: "",
};

const determineClasses = computed(() => [
  typeClasses[type.value],
  sizeClasses[size.value],
]);
</script>
